import { ChevronRight } from '@mui/icons-material'
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  Paper,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { NivosModal } from 'components'
import {
  CreateIncidentReport,
  IncidentReportTable,
  IncidentReportsSearchBar,
  MobileIncidentReportsSearchBar
} from 'components/incident-reports'
import IncidentReportActionButton from 'components/incident-reports/incident-report-action-button'
import IncidentReportDetail from 'components/incident-reports/incident-report-detail'
import NivosPaginator from 'components/nivos-paginator'
import { INCIDENT_REPORT_ADMIN_PERMISSION_CODES } from 'constants/permissions'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { IncidentReport } from 'types/incident-report'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'
import { hasPermissions } from 'utils/user'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  incidentReport: IncidentReportPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    incidentReport: IncidentReportOperations
  }
}

type AppProps = StateProps & DispatchProps

const IncidentReportsFC: React.FC<any> = (props: AppProps) => {
  const { theme, layout, incidentReport, user, operations } = props
  const [selectedReport, setSelectedReport] = React.useState<IncidentReport>()
  const [modalOpen, setModalOpen] = React.useState(false)
  const isMobileView = useMediaQuery('(max-width:1253px)')

  React.useEffect(() => {
    if (incidentReport != null && incidentReport.data.length > 0) {
      if (selectedReport !== undefined) {
        const updatedSelectedReport = incidentReport.data.filter(
          item => item.id === selectedReport.id
        )
        if (updatedSelectedReport.length > 0) {
          setSelectedReport(updatedSelectedReport[0])
          return
        }
      }
    }
    setSelectedReport(incidentReport.data[0])
  }, [incidentReport])

  React.useEffect(() => {
    if (incidentReport.data.length < 1) {
      operations.incidentReport.loading(true)
      void get('/report/').then((data: IncidentReport[]) => {
        const parsedData = data.map(report => {
          return objectifyDateStringFields(report)
        })
        operations.incidentReport.set(parsedData)
        operations.incidentReport.loading(false)
      })
    }
  }, [])

  const ReportItem: React.FC<{ report: IncidentReport }> = props => {
    const { report } = props
    const [hovered, setHovered] = React.useState(
      selectedReport != null ? selectedReport.id === report.id : false
    )

    return (
      <>
        {selectedReport != null && (
          <Box
            padding={1}
            paddingX={2}
            borderBottom={1}
            onClick={() => {
              setModalOpen(true)
              setSelectedReport(report)
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
              cursor: 'pointer'
            }}
            bgcolor={
              selectedReport.id === report.id
                ? theme.quaternaryColor
                : theme.primaryColor
            }
          >
            <Box display={'flex'}>
              <Typography variant={'h5'} flexGrow={1}>
                {report.title}
              </Typography>
              <Box display={'flex'} alignItems={'center'} paddingLeft={1}>
                <Chip
                  variant={'filled'}
                  label={report.status}
                  size={'small'}
                  sx={{
                    minWidth: '93px'
                  }}
                />
              </Box>
            </Box>
            <Divider />
            <Box width={1} display={'flex'}>
              <Box flexGrow={1}>
                <Typography variant={'body2'}>
                  <strong>{layout.language.translations.reporter}</strong>:{' '}
                  {report.createdBy.fullName}
                </Typography>
                <Typography variant={'body2'}>
                  <strong>Date</strong>: {report.createdAt.toUTCString()}
                </Typography>
              </Box>
              <Box paddingTop={1}>
                <IconButton
                  color={'primary'}
                  sx={{
                    marginRight: hovered ? -1 : 0,
                    transition: '0.5s'
                  }}
                >
                  <ChevronRight color={'primary'} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </>
    )
  }

  return (
    <Fade in={true} timeout={1000}>
      <Box width={'100vw'} sx={{ minHeight: 'calc(100vh - 80px)' }}>
        {!isMobileView && (
          <Box
            color={theme.secondaryColor}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            marginX={'auto'}
            width={0.85}
            maxWidth={1253}
            paddingY={2}
          >
            <Box width={1253}>
              <IncidentReportsSearchBar />
            </Box>
            <Box paddingY={2} />
            <Paper elevation={8} sx={{ borderRadius: '12px 0 0 12px' }}>
              <Box display={'flex'}>
                <Box width={0.3} minWidth={380}>
                  {!incidentReport.loading && (
                    <NivosPaginator
                      dataList={
                        incidentReport.data.length > 0
                          ? incidentReport.data
                          : []
                      }
                      childElement={ReportItem}
                      itemsPerPage={10}
                      childPropSetter={(data, index) => {
                        return { report: data, key: index }
                      }}
                    />
                  )}
                  {incidentReport.loading && (
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      width={1}
                      height={1}
                    >
                      <CircularProgress size={15} />
                      <Typography variant={'h5'} marginLeft={1}>
                        Loading...
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box
                  flexGrow={1}
                  paddingX={2}
                  borderLeft={1}
                  minHeight={1000}
                  minWidth={840}
                  maxHeight={'85vh'}
                  overflow={'auto'}
                  width={0.7}
                >
                  {selectedReport != null && !incidentReport.loading && (
                    <Box>
                      <Box
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        padding={2}
                      >
                        <Typography variant={'h3'}>
                          {selectedReport.title}
                        </Typography>
                        {hasPermissions(
                          user,
                          INCIDENT_REPORT_ADMIN_PERMISSION_CODES
                        ) && (
                          <IncidentReportActionButton report={selectedReport} />
                        )}
                      </Box>
                      <Divider />
                      <Box width={1} display={'flex'} justifyContent={'center'}>
                        <IncidentReportDetail />
                      </Box>
                    </Box>
                  )}
                  {(selectedReport == null || incidentReport.loading) && (
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      width={1}
                      height={1}
                    >
                      <CircularProgress size={15} />
                      <Typography variant={'h5'} marginLeft={1}>
                        Loading...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
        {isMobileView && (
          <Box
            width={1}
            paddingTop={1}
            display={'flex'}
            alignItems={'center'}
            flexDirection={'column'}
          >
            <NivosModal
              title={
                selectedReport !== undefined
                  ? selectedReport.title
                  : 'Incident Report'
              }
              open={modalOpen}
              setOpen={setModalOpen}
            >
              {selectedReport != null && !incidentReport.loading && (
                <Box>
                  <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    padding={2}
                  >
                    {hasPermissions(
                      user,
                      INCIDENT_REPORT_ADMIN_PERMISSION_CODES
                    ) && <IncidentReportActionButton report={selectedReport} />}
                  </Box>
                  <Divider />
                  <Box width={1} display={'flex'} justifyContent={'center'}>
                    <IncidentReportDetail />
                  </Box>
                </Box>
              )}
            </NivosModal>
            <Box width={0.9}>
              <MobileIncidentReportsSearchBar />
            </Box>
            <Box width={0.9} marginTop={3}>
              <Paper elevation={8} sx={{ borderRadius: '12px' }}>
                <Box width={1} paddingBottom={1}>
                  {!incidentReport.loading && (
                    <NivosPaginator
                      dataList={
                        incidentReport.data.length > 0
                          ? incidentReport.data
                          : []
                      }
                      childElement={ReportItem}
                      itemsPerPage={10}
                      childPropSetter={(data, index) => {
                        return { report: data, key: index }
                      }}
                    />
                  )}
                  {incidentReport.loading && (
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      width={1}
                      height={1}
                    >
                      <CircularProgress size={15} />
                      <Typography variant={'h5'} marginLeft={1}>
                        Loading...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  )
}

const IncidentReportsV2: React.FC<any> = (props: AppProps) => {
  const { incidentReport, operations } = props
  const navigate = useNavigate()

  React.useEffect(() => {
    if (incidentReport.data.length < 1) {
      operations.incidentReport.loading(true)
      void get('/report/').then((data: IncidentReport[]) => {
        const parsedData = data.map(report => {
          return objectifyDateStringFields(report)
        })
        operations.incidentReport.set(parsedData)
        operations.incidentReport.loading(false)
      })
    }
  }, [])

  return (
    <Box display={'flex'} flexDirection={'column'} width={'99%'}>
      <Box
        display={'flex'}
        flexDirection={'column'}
        padding={4}
        minWidth={'1320px'}
      >
        <Box
          display={'flex'}
          alignItems={'center'}
          paddingRight={4}
          marginY={2}
        >
          <Typography variant={'h4'} padding={1} paddingRight={4}>
            INCIDENT REPORTS
          </Typography>
          <CreateIncidentReport />
        </Box>

        <IncidentReportsSearchBar />

        <Box display={'flex'} overflow={'hidden'} padding={1} margin={1}>
          <IncidentReportTable
            incidentReports={incidentReport.data}
            loading={incidentReport.loading}
            onView={id => navigate(`/incident-reports/${id}`)}
          />
        </Box>
      </Box>
    </Box>
  )
}

console.log(IncidentReportsFC)

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    incidentReport: state.incidentReport
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  incidentReport: incidentReportOperations
})

const IncidentReports = connect(
  mapStateToProps,
  mapDispatchToProps
)(IncidentReportsV2)

export default IncidentReports
